'use client';

import { useEffect, useMemo, useRef } from 'react';
import { WOOSMAP_DEFAULT_MARKER_ICON, WOOSMAP_ID, WOOSMAP_SELECTED_MARKER_ICON } from '@headless-workspace/config';
import { UIStoreInfoCard, useProductDetailContext } from '@headless-workspace/core-ui';
import { LocalityDetails, StoreInfoValue } from '@headless-workspace/domain/common/server';
import { CommonDI } from '@headless-workspace/domain/common/DI';
import { useAppLocale } from '@headless-workspace/domain/common/client';
import { Component } from '@headless-workspace/typings';
import { useDeliveryStoreCookie, useStoreInfoCardUtils } from '../../../hooks';
import { useMap } from './useWoosmapMap';

const MAP_PADDING = { left: 150, right: 150, top: 75, bottom: 75 };
const MAP_ONE_STORE_PADDING = 0.05;

type StoreSelectionMenuMapProps = {
    stores: StoreInfoValue[];
    favoriteStoreId?: string;
    highlightedStoreId?: string;
    setHighlightedStore: (store: StoreInfoValue) => void;
    currentUserPosition: GeolocationPosition | null;
    localityDetails?: LocalityDetails | null;
};

const getPosition = ({ latitude, longitude }: Pick<GeolocationCoordinates, 'latitude' | 'longitude'>) => ({
    lat: latitude,
    lng: longitude,
});

export const StoreSelectionMenuMap: Component<StoreSelectionMenuMapProps> = ({
    stores,
    favoriteStoreId,
    highlightedStoreId,
    setHighlightedStore,
    currentUserPosition,
    localityDetails,
}) => {
    const locale = useAppLocale();
    const { mapStoreInfoToUIStoreInfoCardStore } = useStoreInfoCardUtils();
    const { sku } = useProductDetailContext();

    const selectedMarker = useRef<woosmap.map.Marker | undefined>(undefined);

    const { storeId } = useDeliveryStoreCookie(highlightedStoreId);

    const highlightedStore = useMemo(() => {
        return stores.find((store) => store.id === (highlightedStoreId ?? storeId));
    }, [highlightedStoreId, stores, storeId]);

    const myMap = useMap(document.getElementById(WOOSMAP_ID) as HTMLElement);

    useEffect(() => {
        if (stores.length === 0 && currentUserPosition) {
            myMap?.setCenter(getPosition(currentUserPosition.coords));
        }
    }, [currentUserPosition, myMap, stores]);

    useEffect(() => {
        if (!myMap || selectedMarker.current) {
            return;
        }

        stores.forEach((store) => {
            const isCurrentStoreHighlighted = store.id === (highlightedStoreId ?? storeId);

            const marker = new woosmap.map.Marker({
                position: getPosition(store),
                icon: isCurrentStoreHighlighted ? WOOSMAP_SELECTED_MARKER_ICON : WOOSMAP_DEFAULT_MARKER_ICON,
            });

            marker.setMap(myMap);

            marker.addListener('click', async function () {
                selectedMarker.current?.setIcon(WOOSMAP_DEFAULT_MARKER_ICON);
                marker.setIcon(WOOSMAP_SELECTED_MARKER_ICON);
                selectedMarker.current = marker;
                setHighlightedStore(store);

                await CommonDI.get('DeliveryStoreCookieRepository').setDeliveryStoreCookie(store.id, locale);
            });

            if (isCurrentStoreHighlighted && !selectedMarker.current) {
                selectedMarker.current = marker;
            }
        });

        return () => woosmap.map.event.clearInstanceListeners(myMap);
    }, [stores, selectedMarker, highlightedStoreId, setHighlightedStore, myMap, storeId, locale]);

    // compute bounds of the map
    useEffect(() => {
        if (!myMap) {
            return;
        }
        if (localityDetails && stores.length === 0) {
            myMap.setCenter(getPosition(localityDetails.geometry.location));
            return;
        }

        const bounds = new woosmap.map.LatLngBounds();

        if (localityDetails) {
            const position = getPosition(localityDetails.geometry.location);

            if (stores.length === 1) {
                bounds.extend({ lat: position.lat + MAP_ONE_STORE_PADDING, lng: position.lng + MAP_ONE_STORE_PADDING });
                bounds.extend({ lat: position.lat - MAP_ONE_STORE_PADDING, lng: position.lng - MAP_ONE_STORE_PADDING });
            } else {
                bounds.extend(position);
            }
        }

        stores.forEach((store) => {
            bounds.extend(getPosition(store));
        });

        myMap.fitBounds(bounds, MAP_PADDING);
    }, [myMap, localityDetails]);

    return (
        <section className={'relative flex flex-1 self-stretch p-1'}>
            <div id={WOOSMAP_ID} className={'flex flex-1 self-stretch'} />
            <div className={'absolute left-1 right-1 bottom-1 bg-surface-primary'}>
                {highlightedStore && (
                    <UIStoreInfoCard
                        store={mapStoreInfoToUIStoreInfoCardStore(highlightedStore, sku, favoriteStoreId)}
                    />
                )}
            </div>
        </section>
    );
};
